import { IEventMetadata, IFormFieldMetadata, IFormFieldValueMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, ISourceDataConfigurationFormField, ISourceDataConfigurationFormFieldValue, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion, ISourceDataEvent, ISourceDataField, ISourceDataForm, ITrialFormMetadata } from "@/api/dtos";
import { FC, RefObject } from "react";
import { IHasChildren } from '@ngt/forms';
import { VirtuosoHandle } from "react-virtuoso";

export enum ConfigurationType {
    Event = 1,
    Form = 2,
    Version = 3,
    Section = 4,
    Field = 5,
    Value = 6
}

export const LinkedProperties: Record<ConfigurationType, string[]> = {
    [ConfigurationType.Event]: ['forms'],
    [ConfigurationType.Form]: ['versions'],
    [ConfigurationType.Version]: ['sections', 'fields'],
    [ConfigurationType.Section]: ['fields'],
    [ConfigurationType.Field]: ['values'],
    [ConfigurationType.Value]: []
}

export interface IConfigurationEvent {
    type: ConfigurationType.Event;
    configuration?: ISourceDataConfiguration;
    trialConfiguration?: ISourceDataConfiguration;
    metadata?: ITrialFormMetadata;
    isTrialConfiguration?: boolean;
    isGlobalConfiguration?: boolean;

    formEvents?: Record<string, IConfigurationEvent[]>;
 
    eventKey?: string;
    eventIdentifier?: string;
    eventPropertyPath?: string;
    eventLocalConfiguration?: ISourceDataConfigurationEvent;
    eventTrialConfiguration?: ISourceDataConfigurationEvent;
    eventMetadata?: IEventMetadata;

    forms: IConfigurationForm[];

    order?: any;
}

export interface IConfigurationForm extends Omit<IConfigurationEvent, 'type' | 'forms'> {
    type: ConfigurationType.Form;

    formKey?: string;
    formIdentifier?: string;
    formPropertyPath?: string;
    formTrialConfiguration?: ISourceDataConfigurationForm;
    formMetadata?: IFormMetadata;

    versions: IConfigurationVersion[];

}

export interface IConfigurationVersion extends Omit<IConfigurationForm, 'type' | 'versions'> {
    type: ConfigurationType.Version;

    formVersionKey?: string;
    formVersionIdentifier?: string;
    formVersionPropertyPath?: string;
    formVersionTrialConfiguration?: ISourceDataConfigurationFormVersion;
    formVersionMetadata?: IFormVersionMetadata;

    fields: (IConfigurationSection | IConfigurationField)[];

}

export interface IConfigurationSection extends Omit<IConfigurationVersion, 'type' | 'fields'> {
    type: ConfigurationType.Section;

    formSectionKey?: string;
    formSectionIdentifier?: string;
    formSectionPropertyPath?: string;
    formSectionTrialConfiguration?: ISourceDataConfigurationFormSection;
    formSectionMetadata?: IFormSectionMetadata;

    fields: IConfigurationField[];

}

export interface IConfigurationField extends Omit<IConfigurationSection, 'type' | 'fields'> {
    type: ConfigurationType.Field;

    formFieldKey?: string;
    formFieldIdentifier?: string;
    formFieldPropertyPath?: string;
    formFieldTrialConfiguration?: ISourceDataConfigurationFormField;
    formFieldMetadata?: IFormFieldMetadata;

    values: IConfigurationValue[];
}

export interface IConfigurationValue extends Omit<IConfigurationField, 'type' | 'values'> {
    type: ConfigurationType.Value;

    formFieldValueKey?: string;
    formFieldValueIdentifier?: string;
    formFieldValuePropertyPath?: string;
    formFieldValueTrialConfiguration?: ISourceDataConfigurationFormFieldValue;
    formFieldValueMetadata?: IFormFieldValueMetadata;
}

export interface IConfigurationData {
    events: IConfigurationItem[];
    forms: IConfigurationItem[];
}


export type ISourceDataConfigurationItem = ISourceDataConfigurationEvent | ISourceDataConfigurationForm | ISourceDataConfigurationFormVersion | ISourceDataConfigurationFormSection | ISourceDataConfigurationFormField | ISourceDataConfigurationFormFieldValue;

export type IConfigurationItem = IConfigurationEvent | IConfigurationForm | IConfigurationVersion | IConfigurationSection | IConfigurationField | IConfigurationValue;


export interface IConfigurationEventComponentProps extends IConfigurationEvent, IHasChildren {}
export interface IConfigurationFormComponentProps extends IConfigurationForm, IHasChildren {}
export interface IConfigurationVersionComponentProps extends IConfigurationVersion, IHasChildren {}
export interface IConfigurationSectionComponentProps extends IConfigurationSection, IHasChildren {}
export interface IConfigurationFieldComponentProps extends IConfigurationField, IHasChildren {}
export interface IConfigurationValueComponentProps extends IConfigurationValue, IHasChildren {}

class SourceDataFormProcessor {

    public processConfiguration = async (configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, metadata?: ITrialFormMetadata, isTrialConfiguration?: boolean): Promise<IConfigurationData> => {
        const formEvents: Record<string, IConfigurationEvent[]> = {};
        
        const forms = await this.processAllForms(configuration, isTrialConfiguration ? configuration : trialConfiguration, metadata, isTrialConfiguration);
        const events = await this.processAllEvents(configuration, isTrialConfiguration ? configuration : trialConfiguration, metadata, isTrialConfiguration);

        return {
            events: this.processEventList(events, forms, formEvents),
            forms: this.processFormList(forms, formEvents)
        }
    }

    private processEventList(events: IConfigurationEvent[], forms: Record<string, IConfigurationForm>, formEvents: Record<string, IConfigurationEvent[]>) {
        const eventList: IConfigurationItem[] = [];

        for (let event of events) {
            event.formEvents = formEvents;
            eventList.push(event);
            
            const formKeys = event.eventLocalConfiguration?.forms ? 
                Object.keys(event.eventLocalConfiguration?.forms).map(x => {
                    const formMetadata = event.metadata?.forms?.find(y => y.id === x);
                    const formEventMetadata = event.eventMetadata?.forms?.find(y => y.formMetadataId === formMetadata?.id)

                    return {
                        key: x,
                        order: formEventMetadata?.order ?? 0
                    }
                }) : 
                [];

            for (let formKey of formKeys.sort((a, b) => a.order > b.order ? 1 : -1)) {
                if (!formEvents[formKey.key]) {
                    formEvents[formKey.key] = [];
                }

                formEvents[formKey.key].push(event);

                const form = {
                    ...event,
                    ...forms[formKey.key],
                    isGlobalConfiguration: false
                };

                form.formPropertyPath = `${event.eventPropertyPath}.${form.formPropertyPath}`;

                form.formIdentifier = `${event.eventIdentifier}-${form.formIdentifier}`;

                eventList.push(form);
    
                if (form.versions) {
                    for (let version of form.versions) {
                        version = {
                            ...event,
                            ...version,
                            isGlobalConfiguration: false
                        };
                        version.formPropertyPath = form.formPropertyPath;
                        version.formVersionPropertyPath = `${event.eventPropertyPath}.${version.formVersionPropertyPath}`;

                        
                        version.formIdentifier = form.formIdentifier;
                        version.formVersionIdentifier = `${event.eventIdentifier}-${version.formVersionIdentifier}`;
    
                        eventList.push(version);
    
                        if (version.fields) {
                            for (let fieldOrSection of version.fields) {
                                fieldOrSection = {
                                    ...event,
                                    ...fieldOrSection,
                                    isGlobalConfiguration: false
                                };

                                fieldOrSection.formPropertyPath = form.formPropertyPath;
                                fieldOrSection.formVersionPropertyPath = version.formVersionPropertyPath;

                                
                                fieldOrSection.formIdentifier = form.formIdentifier;
                                fieldOrSection.formVersionIdentifier = version.formVersionIdentifier;
    
                                eventList.push(fieldOrSection);
    
                                if (fieldOrSection.type === ConfigurationType.Section) {
                                    fieldOrSection.formSectionPropertyPath = `${event.eventPropertyPath}.${fieldOrSection.formSectionPropertyPath}`;

                                    fieldOrSection.formSectionIdentifier = `${event.eventIdentifier}-${fieldOrSection.formSectionIdentifier}`;
    
                                    if (fieldOrSection.fields) {
                                        for (let field of fieldOrSection.fields) {
                                            field = {
                                                ...event,
                                                ...field,
                                                isGlobalConfiguration: false
                                            };
                                            field.formPropertyPath = form.formPropertyPath;
                                            field.formVersionPropertyPath = version.formVersionPropertyPath;
                                            field.formSectionPropertyPath = fieldOrSection.formSectionPropertyPath;
                                            field.formFieldPropertyPath = `${event.eventPropertyPath}.${field.formFieldPropertyPath}`;

                                            
                                            field.formIdentifier = form.formIdentifier;
                                            field.formVersionIdentifier = version.formVersionIdentifier;
                                            field.formSectionIdentifier = fieldOrSection.formSectionIdentifier;
                                            field.formFieldIdentifier = `${event.eventIdentifier}-${field.formFieldIdentifier}`;
    
                                            eventList.push(field)
                                            
                                            if (field.values) {
                                                for (let value of field.values) {
                                                    value = {
                                                        ...event,
                                                        ...value,
                                                        isGlobalConfiguration: false
                                                    };
                                                    value.formPropertyPath = form.formPropertyPath;
                                                    value.formVersionPropertyPath = version.formVersionPropertyPath;
                                                    value.formSectionPropertyPath = fieldOrSection.formSectionPropertyPath;
                                                    value.formFieldPropertyPath = field.formFieldPropertyPath;
                                                    value.formFieldValuePropertyPath = `${event.eventPropertyPath}.${value.formFieldValuePropertyPath}`;
                                            
                                                    value.formIdentifier = form.formIdentifier;
                                                    value.formVersionIdentifier = version.formVersionIdentifier;
                                                    value.formSectionIdentifier = fieldOrSection.formSectionIdentifier;
                                                    value.formFieldIdentifier = field.formFieldIdentifier;
                                                    value.formFieldValueIdentifier = `${event.eventIdentifier}-${value.formFieldValueIdentifier}`;
    
                                                    eventList.push(value);
                                                }
                                            }
                                        }
                                    }
                                }
                                else {
                                    fieldOrSection.formFieldPropertyPath = `${event.eventPropertyPath}.${fieldOrSection.formFieldPropertyPath}`;

                                    fieldOrSection.formFieldIdentifier = `${event.eventIdentifier}-${fieldOrSection.formFieldIdentifier}`;
    
                                    if (fieldOrSection.values ) {
                                        for (let value of fieldOrSection.values) {
                                            value = {
                                                ...event,
                                                ...value,
                                                isGlobalConfiguration: false
                                            };

                                            value.formPropertyPath = form.formPropertyPath;
                                            value.formVersionPropertyPath = version.formVersionPropertyPath;
                                            value.formFieldPropertyPath = fieldOrSection.formFieldPropertyPath;
                                            value.formFieldValuePropertyPath = `${event.eventPropertyPath}.${value.formFieldValuePropertyPath}`;
                                            
                                            value.formIdentifier = form.formIdentifier;
                                            value.formVersionIdentifier = version.formVersionIdentifier;
                                            value.formFieldIdentifier = fieldOrSection.formFieldIdentifier;
                                            value.formFieldValueIdentifier = `${event.eventIdentifier}-${value.formFieldValueIdentifier}`;
    
                                            eventList.push(value);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        
        return eventList;
    }
    
    private processFormList(forms: Record<string, IConfigurationForm>, formEvents: Record<string, IConfigurationEvent[]>) {
        const formList: IConfigurationItem[] = [];

        for (let form of Object.values(forms).sort((a, b) => a.order > b.order ? 1 : -1)) {
            form.formPropertyPath = `configuration.${form.formPropertyPath}`;
            form.formEvents = formEvents;
            formList.push(form);

            if (form.versions) {
                for (let version of form.versions) {
                    version.formPropertyPath = form.formPropertyPath;
                    version.formVersionPropertyPath = `configuration.${version.formVersionPropertyPath}`;
                    version.formEvents = formEvents;

                    formList.push(version);

                    if (version.fields) {
                        for (let fieldOrSection of version.fields) {
                            fieldOrSection.formPropertyPath = form.formPropertyPath;
                            fieldOrSection.formVersionPropertyPath = version.formVersionPropertyPath;
                            fieldOrSection.formEvents = formEvents;

                            formList.push(fieldOrSection);

                            if (fieldOrSection.type === ConfigurationType.Section) {
                                fieldOrSection.formSectionPropertyPath = `configuration.${fieldOrSection.formSectionPropertyPath}`;

                                if (fieldOrSection.fields) {
                                    for (let field of fieldOrSection.fields) {
                                        field.formPropertyPath = form.formPropertyPath;
                                        field.formVersionPropertyPath = version.formVersionPropertyPath;
                                        field.formSectionPropertyPath = fieldOrSection.formSectionPropertyPath;
                                        field.formFieldPropertyPath = `configuration.${field.formFieldPropertyPath}`;
                                        field.formEvents = formEvents;

                                        formList.push(field)
                                        
                                        if (field.values) {
                                            for (let value of field.values) {
                                                value.formPropertyPath = form.formPropertyPath;
                                                value.formVersionPropertyPath = version.formVersionPropertyPath;
                                                value.formSectionPropertyPath = fieldOrSection.formSectionPropertyPath;
                                                value.formFieldPropertyPath = field.formFieldPropertyPath;
                                                value.formFieldValuePropertyPath = `configuration.${value.formFieldValuePropertyPath}`;
                                                value.formEvents = formEvents;

                                                formList.push(value);
                                            }
                                        }
                                    }
                                }
                            }
                            else {
                                fieldOrSection.formFieldPropertyPath = `configuration.${fieldOrSection.formFieldPropertyPath}`;

                                if (fieldOrSection.values ) {
                                    for (let value of fieldOrSection.values) {
                                        value.formPropertyPath = form.formPropertyPath;
                                        value.formVersionPropertyPath = version.formVersionPropertyPath;
                                        value.formFieldPropertyPath = fieldOrSection.formFieldPropertyPath;
                                        value.formFieldValuePropertyPath = `configuration.${value.formFieldValuePropertyPath}`;
                                        value.formEvents = formEvents;

                                        formList.push(value);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        
        return formList;
    }

    private processAllEvents = async (configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, metadata?: ITrialFormMetadata, isTrialConfiguration?: boolean) => {
        const eventKeys = configuration?.events ? Object.keys(configuration.events) : [];

        const events: IConfigurationEvent[] = [];

        for (let eventKey of eventKeys) {
           await this.processEvent(eventKey, events, configuration, trialConfiguration, metadata, isTrialConfiguration);
        }

        return events.sort((a, b) => a.order > b.order ? 1 : -1);
    }
    
    private processAllForms = async (configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, metadata?: ITrialFormMetadata, isTrialConfiguration?: boolean) => {
        const formKeys = configuration?.forms ? Object.keys(configuration.forms) : [];

        const forms: Record<string, IConfigurationForm> = {};

        for (let formKey of formKeys) {
            forms[formKey] = await this.processForm(formKey, configuration, trialConfiguration, metadata, isTrialConfiguration);
        }

        return forms;
    }
    
    private processAllVersions = async (form: IConfigurationForm) => {
        const formConfig = form.formTrialConfiguration;
        const versionKeys = formConfig?.versions ? Object.keys(formConfig.versions) : [];

        const versions: IConfigurationVersion[] = [];

        for (let versionKey of versionKeys) {
            await this.processVersion(versionKey, versions, form);
        }

        return versions.sort((a, b) => a.order > b.order ? 1 : -1);
    }
    
    private processAllVersionFields = async (version: IConfigurationVersion ) => {
        const sections: Record<string, boolean> = {};
        const fields: (IConfigurationSection | IConfigurationField)[] = [];

        

        for (let field of (version.formVersionMetadata?.fields ?? [])) {
            if (field.formSectionMetadataId) {
                let section = sections[field.formSectionMetadataId];

                if (!section) {
                    sections[field.formSectionMetadataId] = true;

                    await this.processSection(field.formSectionMetadataId, fields, version);
                }
            }
            else {
                await this.processVersionField(field.id!, fields, version);
            }
        }

        return fields.sort((a, b) => a.order > b.order ? 1 : -1);
    }

    private processAllSectionFields = async (formSection: IConfigurationSection ) => {
        const formConfig = formSection.formSectionTrialConfiguration;
        const fieldKeys = formConfig?.fields ? Object.keys(formConfig.fields) : [];

        const fields: IConfigurationField[] = [];

        for (let fieldKey of fieldKeys) {
            await this.processSectionField(fieldKey, fields, formSection);
        }

        return fields.sort((a, b) => a.order > b.order ? 1 : -1);
    }

    private processAllFieldValues = async (field: IConfigurationField ) => {
        const formConfig = field.formFieldTrialConfiguration;
        const valueKeys = formConfig?.values ? Object.keys(formConfig.values) : [];

        const values: IConfigurationValue[] = [];

        for (let valueKey of valueKeys) {
            await this.processFieldValue(valueKey, values, field);
        }

        return values.sort((a, b) => a.order > b.order ? 1 : -1);
    }
    
    // private processAllFields = async (form: IConfigurationForm) => {
    //     const versionKeys = form.formConfiguration?.versions ? Object.keys(form.formConfiguration.versions) : [];

    //     const versions: IConfigurationVersion[] = [];

    //     for (let versionKey of versionKeys) {
    //         await this.processField(versionKey, versions, form);
    //     }

    //     return versions;
    // }

    
    // private processAllValues = async (field: IConfigurationField) => {
    //     const valueKeys = field.formFieldConfiguration?.values ? Object.keys(field.formFieldConfiguration.values) : [];

    //     const values: IConfigurationValue[] = [];

    //     for (let versionKey of valueKeys) {
    //         await this.processValue(versionKey, values, field);
    //     }

    //     return values;
    // }
    
    private processEvent = async (eventKey: string, events: IConfigurationEvent[], configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, metadata?: ITrialFormMetadata, isTrialConfiguration?: boolean) => {
        
        const eventMetadata = metadata?.events?.find(x => x.id === eventKey);
        const eventLocalConfiguration = configuration!.events![eventKey];
        const eventTrialConfiguration = trialConfiguration?.events![eventKey];

        let eventConfiguration: ISourceDataConfigurationEvent | undefined = undefined;
        let eventConfigurationPropetyPath: string | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!eventLocalConfiguration && !eventLocalConfiguration.linked) {
            eventConfiguration = eventLocalConfiguration;
            eventConfigurationPropetyPath = `configuration.events.${eventKey}`;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else {
            eventConfiguration = eventTrialConfiguration;
            eventConfigurationPropetyPath = `trialConfiguration.events.${eventKey}`;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }

        const event: IConfigurationEvent = {
            type: ConfigurationType.Event,
            configuration: configuration,
            trialConfiguration: trialConfiguration,
            metadata: metadata,
            isTrialConfiguration: isTrialConfiguration,
            isGlobalConfiguration: false,

            eventKey: eventKey,
            eventIdentifier: eventKey,
            eventPropertyPath: `configuration.events.${eventKey}`,
            eventLocalConfiguration: eventLocalConfiguration,
            eventTrialConfiguration: eventTrialConfiguration,
            eventMetadata: eventMetadata,
            forms: [],
            order: eventMetadata?.order
        }

        events.push(event);
    }

    private processForm = async (formKey: string, configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, metadata?: ITrialFormMetadata, isTrialConfiguration?: boolean) => {
        const formMetadata = metadata?.forms?.find(x => x.id === formKey);
        const formTrialConfiguration = trialConfiguration?.forms?.[formKey];


        const form: IConfigurationForm = {
            type: ConfigurationType.Form,
            configuration: configuration,
            trialConfiguration: trialConfiguration,
            metadata: metadata,
            isTrialConfiguration: isTrialConfiguration,
            isGlobalConfiguration: true,

            formKey: formKey,
            formIdentifier: `${formKey}`,
            formPropertyPath: `forms.${formKey}`,
            formMetadata: formMetadata,
            order: formMetadata?.name,
            formTrialConfiguration: formTrialConfiguration,
            versions: []
        }

        form.versions = await this.processAllVersions(form);

        return form;
    };

    private processVersion = async (formVersionKey: string, items: IConfigurationItem[], form: IConfigurationForm) => {
        const formVersionMetadata = form.formMetadata?.versions?.find(x => x.id === formVersionKey);
        const formVersionTrialConfiguration = form.formTrialConfiguration?.versions?.[formVersionKey];

        const version: IConfigurationVersion = {
            ...form,
            type: ConfigurationType.Version,
            formVersionKey: formVersionKey,
            formVersionIdentifier: `${form.formIdentifier}-${formVersionKey}`,
            formVersionPropertyPath: `${form.formPropertyPath}.versions.${formVersionKey}`,
            formVersionTrialConfiguration: formVersionTrialConfiguration,
            formVersionMetadata: formVersionMetadata,
            order: formVersionMetadata?.order,
            fields: []
        }

        version.fields = await this.processAllVersionFields(version);

        items.push(version);
    };

    private processSection = async (formSectionKey: string, items: IConfigurationItem[], formVersion: IConfigurationVersion) => {
        const formSectionMetadata = formVersion.formVersionMetadata?.sections?.find(x => x.id === formSectionKey);
        const formSectionTrialConfiguration = formVersion.formVersionTrialConfiguration?.sections?.[formSectionKey];

        const section: IConfigurationSection = {
            ...formVersion,
            type: ConfigurationType.Section,
            formSectionKey: formSectionKey,
            formSectionIdentifier: `${formVersion.formVersionIdentifier}-${formSectionKey}`,
            formSectionPropertyPath: `${formVersion.formVersionPropertyPath}.sections.${formSectionKey}`,
            formSectionTrialConfiguration: formSectionTrialConfiguration,
            formSectionMetadata: formSectionMetadata,
            fields: []
        }


        section.fields = await this.processAllSectionFields(section);

        section.order = section.fields?.reduce((a, b) => !a ? b.order : b.order > a ? b.order : a, undefined)

        items.push(section);
    };

    private processVersionField = async (formFieldKey: string, fields: IConfigurationItem[], formVersion: IConfigurationVersion) => {
        const formFieldMetadata = formVersion.formVersionMetadata?.fields?.find(x => x.id === formFieldKey);
        const formFieldTrialConfiguration = formVersion.formVersionTrialConfiguration?.fields?.[formFieldKey];

        if (!!formFieldMetadata?.parentFormFieldMetadataId) {
            return;
        }

        const field: IConfigurationField = {
            ...formVersion,
            type: ConfigurationType.Field,
            formFieldKey: formFieldKey,
            formFieldIdentifier: `${formVersion.formVersionIdentifier}-${formFieldKey}`,
            formFieldPropertyPath: `${formVersion.formVersionPropertyPath}.fields.${formFieldKey}`,
            formFieldTrialConfiguration: formFieldTrialConfiguration,
            formFieldMetadata: formFieldMetadata,
            values: [],
            order: formFieldMetadata?.order
        }

        field.values = await this.processAllFieldValues(field);

        fields.push(field);
    };

    private processSectionField = async (formFieldKey: string, items: IConfigurationItem[], formSection: IConfigurationSection) => {
        const formFieldMetadata = formSection.formVersionMetadata?.fields?.find(x => x.id === formFieldKey);
        const formFieldTrialConfiguration = formSection.formSectionTrialConfiguration?.fields?.[formFieldKey];

        if (formFieldMetadata?.parentFormFieldMetadataId) {
            return;
        }

        const field: IConfigurationField = {
            ...formSection,
            type: ConfigurationType.Field,
            formFieldKey: formFieldKey,
            formFieldIdentifier: `${formSection.formSectionIdentifier}-${formFieldKey}`,
            formFieldPropertyPath: `${formSection.formSectionPropertyPath}.fields.${formFieldKey}`,
            formFieldTrialConfiguration: formFieldTrialConfiguration,
            formFieldMetadata: formFieldMetadata,
            values: [],
            order: formFieldMetadata?.order
        }

        field.values = await this.processAllFieldValues(field);

        items.push(field);
    };

    private processFieldValue = async (formFieldValueKey: string, items: IConfigurationItem[], formField: IConfigurationField) => {
        const formFieldValueMetadata = formField.formFieldMetadata?.values?.find(x => x.id === formFieldValueKey);
        const formFieldValueTrialConfiguration = formField.formFieldTrialConfiguration?.values?.[formFieldValueKey];

        const value: IConfigurationValue = {
            ...formField,
            type: ConfigurationType.Value,
            formFieldValueKey: formFieldValueKey,
            formFieldValueIdentifier: `${formField.formFieldIdentifier}-${formFieldValueKey}`,
            formFieldValuePropertyPath: `${formField.formFieldPropertyPath}.values.${formFieldValueKey}`,
            formFieldValueTrialConfiguration: formFieldValueTrialConfiguration,
            formFieldValueMetadata: formFieldValueMetadata,
            order: formFieldValueMetadata?.order
        }

        items.push(value);
    };
}

export default SourceDataFormProcessor;